import { createHash, createHmac, timingSafeEqual } from "node:crypto";

const TOKEN_HEADER = { alg: "HS256", typ: "SQR" } as const;

export const ROADMAP_ACCESS_TOKEN_AUD = "roadmap" as const;

export interface RoadmapAccessTokenClaims {
	waitlistSignupId: string;
	emailHash: string;
	aud: typeof ROADMAP_ACCESS_TOKEN_AUD;
	issuedAtSec: number;
	expiresAtSec: number;
}

function base64UrlEncode(input: string): string {
	return Buffer.from(input, "utf8").toString("base64url");
}

function base64UrlDecode(input: string): string {
	return Buffer.from(input, "base64url").toString("utf8");
}

function getSecret(): string {
	return (
		process.env.ROADMAP_ACCESS_TOKEN_SECRET?.trim() ||
		process.env.AUTH_SECRET?.trim() ||
		"sqx-dev-roadmap-secret"
	);
}

function sign(unsigned: string): string {
	return createHmac("sha256", getSecret())
		.update(unsigned)
		.digest("base64url");
}

/** Stable hash stored in token claims; verified against signup row at use time. */
export function hashEmailForRoadmapAccessToken(
	emailNormalized: string,
): string {
	const salt =
		process.env.ROADMAP_ACCESS_TOKEN_EMAIL_SALT?.trim() ||
		"roadmap-email-v1";
	return createHash("sha256")
		.update(`${salt}:${emailNormalized}`)
		.digest("hex");
}

export function issueRoadmapAccessToken(options: {
	waitlistSignupId: string;
	emailNormalized: string;
	ttlMs: number;
	now?: Date;
}): { token: string; claims: RoadmapAccessTokenClaims } {
	const now = options.now ?? new Date();
	const nowSec = Math.floor(now.getTime() / 1000);
	const ttlSec = Math.max(60, Math.floor(options.ttlMs / 1000));
	const emailHash = hashEmailForRoadmapAccessToken(options.emailNormalized);
	const claims: RoadmapAccessTokenClaims = {
		waitlistSignupId: options.waitlistSignupId,
		emailHash,
		aud: ROADMAP_ACCESS_TOKEN_AUD,
		issuedAtSec: nowSec,
		expiresAtSec: nowSec + ttlSec,
	};
	const headerPart = base64UrlEncode(JSON.stringify(TOKEN_HEADER));
	const claimsPart = base64UrlEncode(JSON.stringify(claims));
	const unsigned = `${headerPart}.${claimsPart}`;
	const signaturePart = sign(unsigned);
	return {
		token: `${unsigned}.${signaturePart}`,
		claims,
	};
}

export type VerifyRoadmapAccessTokenFailureReason =
	| "invalid_format"
	| "invalid_signature"
	| "invalid_claims"
	| "expired"
	| "aud_mismatch";

export type VerifyRoadmapAccessTokenResult =
	| { ok: true; claims: RoadmapAccessTokenClaims }
	| { ok: false; reason: VerifyRoadmapAccessTokenFailureReason };

function parseToken(
	token: string,
): { unsigned: string; claims: RoadmapAccessTokenClaims } | null {
	const parts = token.split(".");
	if (parts.length !== 3) {
		return null;
	}
	const [headerPart, claimsPart] = parts;
	try {
		const header = JSON.parse(base64UrlDecode(headerPart)) as {
			alg?: string;
			typ?: string;
		};
		if (header.alg !== "HS256" || header.typ !== "SQR") {
			return null;
		}
		const claims = JSON.parse(
			base64UrlDecode(claimsPart),
		) as RoadmapAccessTokenClaims;
		if (
			typeof claims.waitlistSignupId !== "string" ||
			typeof claims.emailHash !== "string" ||
			claims.aud !== ROADMAP_ACCESS_TOKEN_AUD ||
			typeof claims.issuedAtSec !== "number" ||
			typeof claims.expiresAtSec !== "number"
		) {
			return null;
		}
		return { unsigned: `${headerPart}.${claimsPart}`, claims };
	} catch {
		return null;
	}
}

export function verifyRoadmapAccessToken(
	token: string,
	now?: Date,
): VerifyRoadmapAccessTokenResult {
	const parsed = parseToken(token);
	if (!parsed) {
		return { ok: false, reason: "invalid_format" };
	}
	const sigPart = token.split(".")[2] ?? "";
	const expectedSig = sign(parsed.unsigned);
	const left = Buffer.from(sigPart, "utf8");
	const right = Buffer.from(expectedSig, "utf8");
	if (left.length !== right.length || !timingSafeEqual(left, right)) {
		return { ok: false, reason: "invalid_signature" };
	}
	const nowSec = Math.floor((now ?? new Date()).getTime() / 1000);
	if (parsed.claims.expiresAtSec < nowSec) {
		return { ok: false, reason: "expired" };
	}
	if (parsed.claims.aud !== ROADMAP_ACCESS_TOKEN_AUD) {
		return { ok: false, reason: "aud_mismatch" };
	}
	return { ok: true, claims: parsed.claims };
}
